import React from 'react';
import { FormattedMessage } from 'react-intl';

export interface FeaturesProps {
  featuresRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Six capability cards (handoff §4.3). Per user override, the "Carga subjetiva
 * y progresión" card replaces the handoff's "RPE y progresión" since RPE is
 * just one of several subjective methods (RIR, frecuencia cardíaca, etc.).
 * Pills inside mocks keep "RPE 8" — that's the technical label coaches see.
 */
const Features = ({ featuresRef }: FeaturesProps) => {
  return (
    <section className="sec sec-features light" id="features" data-screen-label="03 Features" ref={featuresRef}>
      <div className="container">
        <div className="sec-head">
          <div className="eyebrow on-light">
            <span className="eyebrow-dot" />
            <FormattedMessage id="landing.features.eyebrow" defaultMessage="PRODUCTO" />
          </div>
          <h2 className="h2">
            <FormattedMessage
              id="landing.features.title"
              defaultMessage="Todo lo que necesitás, sin nada que te estorbe."
            />
          </h2>
          <p className="lede">
            <FormattedMessage
              id="landing.features.lede"
              defaultMessage="Seis herramientas, una sola interfaz. Cada una pensada para el trabajo real del entrenador."
            />
          </p>
        </div>

        <div className="features-grid">
          {/* 1. Planificación de ciclos */}
          <article className="feature-card">
            <div className="feature-mock">
              <div className="mock-cycle">
                <div className="week lime" data-w="S1" style={{ height: '38%' }} />
                <div className="week lime" data-w="S2" style={{ height: '56%' }} />
                <div className="week partial" data-w="S3" style={{ height: '78%' }} />
                <div className="week" data-w="S4" style={{ height: '42%' }} />
                <div className="week" data-w="S5" style={{ height: '60%' }} />
                <div className="week" data-w="S6" style={{ height: '82%' }} />
                <div className="week" data-w="S7" style={{ height: '94%' }} />
                <div className="week" data-w="S8" style={{ height: '32%' }} />
              </div>
            </div>
            <h3>
              <FormattedMessage id="landing.features.1.title" defaultMessage="Planificación de ciclos" />
            </h3>
            <p>
              <FormattedMessage
                id="landing.features.1.desc"
                defaultMessage="Diseñá mesociclos de 4, 8 o 16 semanas. Acumulación, intensificación, descarga — todo en una grilla visual."
              />
            </p>
          </article>

          {/* 2. Editor de planes */}
          <article className="feature-card">
            <div className="feature-mock">
              <div className="mock-editor">
                <div className="row">
                  <div className="bar" />
                  <div className="name">Sentadilla</div>
                  <div className="meta">4×6 · RPE 8</div>
                </div>
                <div className="row">
                  <div className="bar b2" />
                  <div className="name">Press banca</div>
                  <div className="meta">5×5 · RPE 7.5</div>
                </div>
                <div className="row">
                  <div className="bar b3" />
                  <div className="name">Remo con barra</div>
                  <div className="meta">4×8 · RPE 7</div>
                </div>
                <div className="row">
                  <div className="bar" />
                  <div className="name">Dominadas</div>
                  <div className="meta">3×6 · +10kg</div>
                </div>
              </div>
            </div>
            <h3>
              <FormattedMessage id="landing.features.2.title" defaultMessage="Editor de planes" />
            </h3>
            <p>
              <FormattedMessage
                id="landing.features.2.desc"
                defaultMessage="Arrastrá ejercicios, definí sets, reps, carga y tempo. Guardá plantillas y reutilizalas con cualquier atleta."
              />
            </p>
          </article>

          {/* 3. Carga subjetiva y progresión (override del handoff "RPE y progresión") */}
          <article className="feature-card">
            <div className="feature-mock">
              <div className="mock-rpe">
                <div className="label">PROMEDIO POR SEMANA</div>
                <div className="bars">
                  <div className="b" style={{ height: '40%' }} />
                  <div className="b" style={{ height: '56%' }} />
                  <div className="b" style={{ height: '72%', background: '#a3e635' }} />
                  <div className="b" style={{ height: '64%' }} />
                  <div className="b" style={{ height: '86%', background: '#a3e635' }} />
                  <div className="b" style={{ height: '92%', background: '#a3e635' }} />
                  <div className="b" style={{ height: '48%' }} />
                </div>
                <div className="axis">
                  <span>S1</span>
                  <span>S2</span>
                  <span>S3</span>
                  <span>S4</span>
                  <span>S5</span>
                  <span>S6</span>
                  <span>S7</span>
                </div>
              </div>
            </div>
            <h3>
              <FormattedMessage id="landing.features.3.title" defaultMessage="Carga subjetiva y progresión" />
            </h3>
            <p>
              <FormattedMessage
                id="landing.features.3.desc"
                defaultMessage="Cargá esfuerzo percibido (RPE, RIR, frecuencia cardíaca) sesión por sesión. TrainEase te muestra cuándo apretar y cuándo soltar."
              />
            </p>
          </article>

          {/* 4. Sesión en vivo */}
          <article className="feature-card">
            <div className="feature-mock">
              <div className="mock-session">
                <div className="top">
                  <div className="name">Set actual · Martina S.</div>
                  <div className="live">EN VIVO</div>
                </div>
                <div className="set done">
                  <div className="check" />
                  <div className="lbl">Set 1</div>
                  <div className="val">6 × 140</div>
                </div>
                <div className="set done">
                  <div className="check" />
                  <div className="lbl">Set 2</div>
                  <div className="val">6 × 140</div>
                </div>
                <div className="set">
                  <div className="check" />
                  <div className="lbl">Set 3 · en curso</div>
                  <div className="val">— × 140</div>
                </div>
              </div>
            </div>
            <h3>
              <FormattedMessage id="landing.features.4.title" defaultMessage="Sesión en vivo" />
            </h3>
            <p>
              <FormattedMessage
                id="landing.features.4.desc"
                defaultMessage="Tu atleta marca sets desde su navegador. Vos los ves en tiempo real, sin instalar nada ni mandar mensajes."
              />
            </p>
          </article>

          {/* 5. Atletas conectados */}
          <article className="feature-card">
            <div className="feature-mock">
              <div className="mock-athletes">
                <div className="ath live">
                  <div className="av" style={{ background: 'linear-gradient(135deg,#a3e635,#65a30d)' }}>
                    MS
                  </div>
                  <div className="info">
                    <div className="name">Martina S.</div>
                    <div className="stat">
                      <span className="d" />
                      Entrenando
                    </div>
                  </div>
                </div>
                <div className="ath">
                  <div className="av" style={{ background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)' }}>
                    LP
                  </div>
                  <div className="info">
                    <div className="name">Lucas P.</div>
                    <div className="stat">
                      <span className="d" />
                      Completó · 18min
                    </div>
                  </div>
                </div>
                <div className="ath">
                  <div className="av" style={{ background: 'linear-gradient(135deg,#f97316,#c2410c)' }}>
                    RK
                  </div>
                  <div className="info">
                    <div className="name">Rocío K.</div>
                    <div className="stat">
                      <span className="d" />
                      Programado
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <h3>
              <FormattedMessage id="landing.features.5.title" defaultMessage="Atletas conectados" />
            </h3>
            <p>
              <FormattedMessage
                id="landing.features.5.desc"
                defaultMessage="Vista única de todos tus atletas. Quién entrenó hoy, quién se atrasó, quién mejoró. Adherencia visible."
              />
            </p>
          </article>

          {/* 6. Progreso medido */}
          <article className="feature-card">
            <div className="feature-mock">
              <div className="mock-progress">
                <div className="head">
                  <span className="label">VOLUMEN MENSUAL · MARTINA S.</span>
                </div>
                <div className="head">
                  <span className="val">
                    14.2t<span className="up">+ 18%</span>
                  </span>
                </div>
                <div className="chart">
                  <svg viewBox="0 0 220 60" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="prGradLanding" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#a3e635" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#a3e635" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M 0 50 L 30 44 L 60 36 L 90 40 L 120 28 L 150 22 L 180 16 L 220 8 L 220 60 L 0 60 Z"
                      fill="url(#prGradLanding)"
                    />
                    <path
                      d="M 0 50 L 30 44 L 60 36 L 90 40 L 120 28 L 150 22 L 180 16 L 220 8"
                      stroke="#a3e635"
                      strokeWidth="2.5"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="220" cy="8" r="3.5" fill="#a3e635" />
                    <circle cx="220" cy="8" r="6" fill="#a3e635" opacity="0.3" />
                  </svg>
                </div>
              </div>
            </div>
            <h3>
              <FormattedMessage id="landing.features.6.title" defaultMessage="Progreso medido" />
            </h3>
            <p>
              <FormattedMessage
                id="landing.features.6.desc"
                defaultMessage="Volumen, PRs, adherencia, evolución. Cada número con contexto histórico — sabés si va para arriba o se trabó."
              />
            </p>
          </article>
        </div>
      </div>
    </section>
  );
};

export default Features;
